import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useState } from "react";
import {
  AdminModal,
  adminModalInputClass,
  adminModalLabelClass,
} from "../custom/AdminModal";
import { useAdminTranslation } from "../i18n/useAdminTranslation";

type DeleteEventModalProps = {
  open: boolean;
  onClose: () => void;
  eventName: string;
  onConfirm: (confirmName: string) => Promise<boolean>;
};

export function DeleteEventModal({
  open,
  onClose,
  eventName,
  onConfirm,
}: DeleteEventModalProps) {
  const { t } = useAdminTranslation();
  const [typedName, setTypedName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }
    setTypedName("");
    onClose();
  }, [loading, onClose]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      const ok = await onConfirm(typedName.trim());
      if (ok) {
        setTypedName("");
        onClose();
      }
    } finally {
      setLoading(false);
    }
  }, [onClose, onConfirm, typedName]);

  const canConfirm = typedName.trim() === eventName.trim();

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      ariaLabel={t("deleteEvent.ariaLabel")}
      title={t("deleteEvent.title")}
      description={
        <>
          {t("deleteEvent.description")}{" "}
          <strong>{eventName}</strong>
        </>
      }
      footer={
        <>
          {loading ? <CircularProgress size="small" /> : null}
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            {t("deleteEvent.cancel")}
          </Button>
          <Button
            disabled={loading || !canConfirm}
            onClick={() => void handleConfirm()}
            variant="filled"
            color="error"
          >
            {t("deleteEvent.confirm")}
          </Button>
        </>
      }
    >
      <label className={adminModalLabelClass} htmlFor="delete-event-confirm">
        {t("deleteEvent.confirmLabel")} {eventName}
      </label>
      <input
        id="delete-event-confirm"
        className={adminModalInputClass}
        value={typedName}
        onChange={(event) => setTypedName(event.target.value)}
        disabled={loading}
        autoComplete="off"
      />
    </AdminModal>
  );
}
